/**
 * usePostalCodeLookup — consumer hook for PostalCodeLookupPort.
 *
 * Reads the postalCodeLookup port from PortsContext and returns a callback that
 * forwards a postal code to `port.lookupCity`. The lookup is optional by
 * contract: the contact-update form works without it. So unlike the other
 * consumer hooks in this module, this one does NOT throw when the slot (or the
 * provider) is absent — it returns a callback that yields `{ city: null }`, i.e.
 * no autofill. A missing port degrades the form to "type your own city", never
 * to a crash.
 *
 * Passthrough only (mirrors member-profile/useUpdateContactDetails): the
 * callback forwards straight to the port; the form owns the autofill decision
 * (it only overwrites an empty/untouched city, and `null` never overwrites a
 * typed value). The callback is wrapped in `useMemo` keyed on the resolved port
 * so its identity stays stable across re-renders.
 *
 * Distinct from the providers/usePostalCodeLookupPort.ts FACTORY hook: that one
 * builds the demo adapter for the composition root, this one consumes it.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Contract: specs/026-proto-convergence-v2/contracts/postal-code-lookup-port.md
 */

import { useContext, useMemo } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type { PostalCodeLookupResult } from './PostalCodeLookupPort.js';

export type LookupCity = (
  postalCode: string,
) => PostalCodeLookupResult | PromiseLike<PostalCodeLookupResult>;

export function usePostalCodeLookup(): LookupCity {
  const ports = useContext(PortsContext);
  // Tolerate both an absent provider (`ports === null`) and an absent slot:
  // the form works without the port, so we resolve to `undefined` rather than
  // throw, and hand back a no-autofill callback below.
  const port = ports?.postalCodeLookup;

  // react-compiler-exception: callback identity must stay stable per port so consumers can use it as an effect dependency
  return useMemo<LookupCity>(() => {
    if (port === undefined) {
      // No port wired → no autofill. A constant `{ city: null }` keeps the
      // result shape uniform so the form never overwrites the member's value.
      return () => ({ city: null });
    }
    return (postalCode) => port.lookupCity(postalCode);
  }, [port]);
}
